import React, { useState } from "react";
import { FileText, Upload, X } from "lucide-react";
import { cn } from "@shared/lib/utils";
import { Button } from "@shared/ui/button";
import {
  useReuploadConsumerDocument,
  useUpdateConsumerDocument,
} from "../../hooks";

interface DocumentItemProps {
  name: string;
  doc: any[];
  uploadDate: string;
  consumerStatus: string;
  documentId: string;
  fileUrl?: string;
  onVerify?: (docId: string) => void;
  onReject?: (docId: string) => void;
  onView?: (docId: string) => void;
  className?: string;
}

interface RejectedDocData {
  id: string;
  documentType: string;
  documentSubtype?: string;
  createdDate: string;
}

const DocumentItem = ({
  name,
  doc,
  uploadDate,
  consumerStatus,
  documentId,
  fileUrl,
  onVerify,
  onReject,
  onView,
  className,
}: DocumentItemProps) => {
  const updateDocumentMutation = useUpdateConsumerDocument();
  const reuploadDocumentMutation = useReuploadConsumerDocument();
  const [loadingStates, setLoadingStates] = useState<
    Record<string, "verify" | "reject" | "reupload" | null>
  >({});
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [selectedDocForReupload, setSelectedDocForReupload] =
    useState<RejectedDocData | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Verified":
        return (
          <span className="h-5 w-5 flex items-center justify-center text-green-600 font-bold">
            ✓
          </span>
        );
      case "Rejected":
        return (
          <span className="h-5 w-5 flex items-center justify-center text-red-600 font-bold">
            ✗
          </span>
        );
      case "Pending":
        return (
          <span className="h-5 w-5 flex items-center justify-center text-yellow-600 font-bold">
            ?
          </span>
        );
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "Verified":
        return "Verified";
      case "Rejected":
        return "Rejected";
      case "Pending":
        return "Pending";
      default:
        return status;
    }
  };

  const handleVerify = async (docId: string) => {
    if (!docId) return;

    try {
      setLoadingStates((prev) => ({ ...prev, [docId]: "verify" }));

      await updateDocumentMutation.mutateAsync({
        document_id: docId,
        payload: {
          action: 1,
        } as any,
      });

      onVerify?.(docId);
    } catch (error) {
      console.error("Error verifying document:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [docId]: null }));
    }
  };

  const handleReject = async (docId: string, docData: any) => {
    if (!docId) return;

    try {
      setLoadingStates((prev) => ({ ...prev, [docId]: "reject" }));

      // Store document data before rejecting
      const rejectedDocInfo: RejectedDocData = {
        id: docId,
        documentType: docData.documentType,
        documentSubtype: docData.documentSubtype,
        createdDate: docData.createdDate,
      };

      await updateDocumentMutation.mutateAsync({
        document_id: docId,
        payload: {
          action: 2,
        } as any,
      });

      onReject?.(docId);
    } catch (error) {
      console.error("Error rejecting document:", error);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [docId]: null }));
    }
  };

  const handleView = (docData: any) => {
    const urlToOpen = docData.file || fileUrl;
    if (urlToOpen) {
      window.open(urlToOpen, "_blank", "noopener,noreferrer");
    } else {
      console.warn("No file URL provided for document");
    }

    onView?.(docData.id);
  };

  const openReuploadModal = (docData: any) => {
    const rejectedDoc: RejectedDocData = {
      id: docData.id,
      documentType: docData.documentType,
      documentSubtype: docData.documentSubtype,
      createdDate: docData.createdDate,
    };

    setSelectedDocForReupload(rejectedDoc);
    setShowReuploadModal(true);
    setSelectedFile(null);
  };

  const closeReuploadModal = () => {
    setShowReuploadModal(false);
    setSelectedDocForReupload(null);
    setSelectedFile(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleReupload = async () => {
    if (!selectedDocForReupload || !selectedFile) {
      console.warn("No document or file selected for reupload");
      return;
    }

    try {
      setLoadingStates((prev) => ({
        ...prev,
        [selectedDocForReupload.id]: "reupload",
      }));

      // Create FormData payload
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("status", "0");

      await reuploadDocumentMutation.mutateAsync({
        documentId: selectedDocForReupload?.id,
        formData,
      });

      closeReuploadModal();
    } catch (error) {
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        [selectedDocForReupload.id]: null,
      }));
    }
  };

  const uniqueDocs = React.useMemo(() => {
    if (!Array.isArray(doc)) return [];

    return Array.from(
      new Map(
        doc.map((item: any) => [
          `${item.documentType}_${item.createdDate}_${item.id}`,
          item,
        ])
      ).values()
    );
  }, [doc]);

  if (!uniqueDocs.length) {
    return <div className="text-gray-500">No documents available</div>;
  }
  const getFileNameFromUrl = (url: string) => url?.split("/")?.pop() ?? "URL not found";
  return (
    <>
      <div className={cn("space-y-4", className)}>
        {uniqueDocs.map((data: any) => {
          const isVerifying = loadingStates[data.id] === "verify";
          const isRejecting = loadingStates[data.id] === "reject";
          const isReuploading = loadingStates[data.id] === "reupload";
          const isLoading = isVerifying || isRejecting || isReuploading;

          return (
            <div
              key={`${data.id}_${data.documentType}_${data.createdDate}`}
              className="flex items-center p-3 border rounded-lg bg-white shadow-sm"
            >
              <div className="mr-3 text-gray-400 flex-shrink-0">
                <FileText className="h-6 w-6" />
              </div>

              <div className="flex-grow min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {data.documentType} - {getFileNameFromUrl(data.file)}
                </div>
                <div className="text-xs text-gray-500">
                  Uploaded on {data.createdDate}
                </div>
                {(data.referenceId || data.reference_id) && (
                  <div className="text-xs text-gray-500 mt-1">
                    Reference ID: {data.referenceId || data.reference_id}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="flex items-center">
                  {getStatusIcon(data.statusDisplay)}
                  <span className="text-sm ml-1 font-medium">
                    {getStatusText(data.statusDisplay)}
                  </span>
                </div>

                <div className="flex gap-2">
                  {data.statusDisplay === "Pending" && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-600 hover:bg-green-50"
                        onClick={() => handleVerify(data.id)}
                        disabled={isLoading}
                      >
                        {isVerifying ? "Verifying..." : "Verify"}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                        onClick={() => handleReject(data.id, data)}
                        disabled={isLoading}
                      >
                        {isRejecting ? "Rejecting..." : "Reject"}
                      </Button>
                    </>
                  )}

                  {data.statusDisplay === "Rejected" && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                      onClick={() => openReuploadModal(data)}
                      disabled={isReuploading}
                    >
                      {isReuploading ? (
                        "Reuploading..."
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-1" />
                          Re-Upload
                        </>
                      )}
                    </Button>
                  )}

                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleView(data)}
                    disabled={!data.file && !fileUrl}
                    className="hover:bg-gray-50"
                  >
                    View
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reupload Modal */}
      {showReuploadModal && selectedDocForReupload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Reupload Document
              </h3>
              <button
                onClick={closeReuploadModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Document Type:</strong>{" "}
                {selectedDocForReupload.documentType}
              </p>
              {selectedDocForReupload.documentSubtype && (
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Document Subtype:</strong>{" "}
                  {selectedDocForReupload.documentSubtype}
                </p>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select File
              </label>
              <input
                type="file"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFile.name}
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                variant="outline"
                onClick={closeReuploadModal}
                disabled={
                  loadingStates[selectedDocForReupload.id] === "reupload"
                }
              >
                Cancel
              </Button>
              <Button
                onClick={handleReupload}
                isLoading={
                  loadingStates[selectedDocForReupload.id] === "reupload"
                }
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {loadingStates[selectedDocForReupload.id] === "reupload" ? (
                  "Uploading..."
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DocumentItem;
